const METADATA_LABEL =
  /^\s*(?:[*_#>\s]*)\s*(?:የ)?(?:reporting\s+)?(?:period|time\s*period|geography|scope|region|language|output\s+format|report\s*format|report\s*type|template|report\s*title|title)\s*(?:[*_]*)\s*[:.)\-–]\s*/i;

const HEADING_PREFIX = /^\s*(?:section\s+)?(?:\d+|[ivxlcdm]+)[\.\)\-:]\s*/i;

const normaliseHeading = (value: string): string =>
  value
    .replace(/[*_`#]/g, "")
    .replace(HEADING_PREFIX, "")
    .replace(/^\s*(?:slide|page)\s*\d+\s*[:.)-]?\s*/i, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .toLowerCase();

const stripInlineMarkdown = (value: string): string =>
  value
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/(^|\s)\*([^*\n]+)\*/g, "$1$2")
    .replace(/`/g, "");

const stripLeadingMetaLines = (value: string): string => {
  const lines = value.split(/\r?\n/);
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }
    if (METADATA_LABEL.test(line)) {
      index += 1;
      continue;
    }
    break;
  }
  return lines.slice(index).join("\n");
};

/**
 * Model output often opens by echoing the requested period, geography and format, or by
 * repeating the section heading it was given. Those lines are rendered by the header, so
 * they are dropped here instead of leaking into the document body.
 */
export const stripLeadingDuplicateTitle = (value: string, title: string): string => {
  const target = normaliseHeading(title ?? "");
  if (!target) return value;

  const lines = value.split(/\r?\n/);
  let index = 0;
  while (index < lines.length && !lines[index].trim()) index += 1;

  if (index >= lines.length) return value;
  if (normaliseHeading(lines[index]) !== target) return value;

  lines.splice(index, 1);
  return lines.join("\n");
};

/** Cleans section prose for the document body while keeping its line structure. */
export const cleanSectionText = (value: string): string =>
  stripLeadingMetaLines(stripInlineMarkdown(value ?? ""))
    .replace(/```[\s\S]*?```/g, "")
    .replace(/^\s*#{1,6}\s+/gm, "")
    .replace(/^\s*(?:slide|page)\s*\d+\s*[:.)-]?\s*/gim, "")
    .replace(/^\s*(?:title|subtitle|speaker\s*notes?|notes?|content)\s*[:.)-]\s*/gim, "")
    .split("\n")
    .map((line) => line.replace(/^\s*[-*•]+\s+/, "").replace(/[ \t]+/g, " ").trimEnd())
    .filter((line, index, all) => line.trim() !== "" || all[index - 1]?.trim() !== "")
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/** Single-line variant used for preview copy, chips and slide bullets. */
export const cleanSectionContent = (value: string): string =>
  cleanSectionText(value)
    .replace(/\|/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Cleans a section for display, dropping any leading echo of its own heading. */
export const cleanSectionBody = (value: string, title: string): string =>
  cleanSectionText(stripLeadingDuplicateTitle(value ?? "", title));

export const cleanSectionSummary = (value: string, title: string): string =>
  cleanSectionContent(stripLeadingDuplicateTitle(value ?? "", title));
